var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { EDITABLE_GLOBAL_ROLES } from "./authorityTypes";
import { NON_STANDARD, classifyAssignment, isNkRole, rowsOfType, } from "./nkRoles";
var requireId = function (id, authorityType) {
    if (!id) {
        throw new Error("Missing ID for authority type role: ".concat(authorityType));
    }
    return id;
};
/**
 * Computes the set of role mutations needed to transition from the original
 * user state to the submitted form state.
 *
 * Used in UserForm onSubmit (US-02). Normally the mutations run after the main PUT; when the
 * organization changes (CZNA-1598) the ones that must precede the move — Supervizor NK changes,
 * since the backend refuses to move an NK supervisor out of NK — run before it and are undone
 * if the PUT fails, the rest after it.
 * Kept separate so it can be unit-tested without rendering the form.
 *
 * `authorityTypeCodes` are the types the form rendered (CZNA-650: the server list plus any
 * type the user already holds). Only those are diffed — a code the form never showed has no
 * form value to compare, and reading its absence as "cleared" would revoke a grant nobody
 * touched.
 *
 * CZNA-1598: a type is diffed by its NK role, never by the row's organization — when the
 * user moves to another organization the server moves their local roles along, so an
 * organization change alone must produce no role call. A NON-STANDARD type (see
 * `classifyAssignment`) is left alone while its sentinel stays selected; any other choice
 * replaces it: every stored row of the type is removed and the picked NK role, if any, is
 * added. The removals come first in the list, and UserForm runs them before the add — the
 * server refuses a second row for the same type.
 */
export function diffRoles(originalRoles, originalAuthorityTypeRoles, formGlobalRoles, formAuthorityTypeRoles, authorityTypeCodes) {
    var _a;
    var mutations = [];
    var _loop_1 = function (role) {
        var wasAssigned = (_a = originalRoles === null || originalRoles === void 0 ? void 0 : originalRoles.some(function (r) { return r.role === role; })) !== null && _a !== void 0 ? _a : false;
        var isAssigned = formGlobalRoles[role];
        if (!wasAssigned && isAssigned)
            mutations.push({ type: "addGlobal", role: role });
        if (wasAssigned && !isAssigned)
            mutations.push({ type: "removeGlobal", role: role });
    };
    // Global roles diff — only editable roles; USER role is managed by the backend
    for (var _i = 0, EDITABLE_GLOBAL_ROLES_1 = EDITABLE_GLOBAL_ROLES; _i < EDITABLE_GLOBAL_ROLES_1.length; _i++) {
        var role = EDITABLE_GLOBAL_ROLES_1[_i];
        _loop_1(role);
    }
    // Authority type roles diff
    // No form value at all is "not on the form", not "cleared" — only "" clears. The
    // distinction matters now that the key set is dynamic: treating a missing key as "" would
    // turn any render/reset timing slip into a revocation. The non-standard sentinel means
    // "leave these rows as they are".
    var touchedTypes = authorityTypeCodes.filter(function (authorityType) {
        var value = formAuthorityTypeRoles[authorityType];
        return value !== undefined && value !== NON_STANDARD;
    });
    var _loop_2 = function (authorityType) {
        // "" (cleared) or an NK role — the filter above took out the rest.
        var value = formAuthorityTypeRoles[authorityType];
        var newRole = isNkRole(value) ? value : undefined;
        var original = classifyAssignment(rowsOfType(originalAuthorityTypeRoles, authorityType));
        if (original.kind === "nonStandard") {
            original.rows.forEach(function (row) {
                return mutations.push({
                    type: "removeAuthorityType",
                    roleId: requireId(row.id, authorityType),
                });
            });
            if (newRole) {
                mutations.push({ type: "addAuthorityType", authorityType: authorityType, nkRole: newRole });
            }
        }
        else if (original.kind === "none") {
            if (newRole) {
                mutations.push({ type: "addAuthorityType", authorityType: authorityType, nkRole: newRole });
            }
        }
        else if (!newRole) {
            mutations.push({
                type: "removeAuthorityType",
                roleId: requireId(original.row.id, authorityType),
            });
        }
        else if (original.nkRole !== newRole) {
            mutations.push({
                type: "updateAuthorityType",
                roleId: requireId(original.row.id, authorityType),
                authorityType: authorityType,
                nkRole: newRole,
                validFrom: original.row.validFrom,
                validTo: original.row.validTo,
            });
        }
    };
    for (var _b = 0, touchedTypes_1 = touchedTypes; _b < touchedTypes_1.length; _b++) {
        var authorityType = touchedTypes_1[_b];
        _loop_2(authorityType);
    }
    return mutations;
}
/** A stored validity window, with only the bounds that are set (an unset bound stays unset). */
var validityOf = function (stored) { return (__assign(__assign({}, (stored.validFrom ? { validFrom: stored.validFrom } : {})), (stored.validTo ? { validTo: stored.validTo } : {}))); };
/**
 * The mutation that undoes `mutation` once it went through — or `undefined` when the NK API
 * cannot put the previous state back (CZNA-1598 review). `result` is what the call answered
 * (an add answers the created row, whose id the undo deletes); `baseline` is the user as
 * stored before the save — its global roles and authority-type rows, validity included.
 *
 * <p>A removed or changed row comes back only when its type was a standard assignment: the
 * API writes NK roles, so a row of a non-standard assignment (a national cataloguer, a role
 * for a foreign library, one of two rows of a type) cannot be recreated as it was. The caller
 * reports those instead of pretending.
 */
export function inverseMutation(mutation, result, baseline) {
    var _a, _b;
    var baselineRows = baseline === null || baseline === void 0 ? void 0 : baseline.authorityTypeRoles;
    var standardRow = function (roleId) {
        var row = baselineRows === null || baselineRows === void 0 ? void 0 : baselineRows.find(function (r) { return r.id === roleId; });
        if (!(row === null || row === void 0 ? void 0 : row.authorityType))
            return undefined;
        var assignment = classifyAssignment(rowsOfType(baselineRows, row.authorityType));
        return assignment.kind === "standard" ? assignment : undefined;
    };
    switch (mutation.type) {
        case "addGlobal":
            return { type: "removeGlobal", role: mutation.role };
        case "removeGlobal": {
            // Back with the window it had — re-added without it, a time-limited or already
            // expired role would become permanent. A role the baseline does not hold has no
            // window to restore, so it is reported rather than re-added wider.
            var stored = (_a = baseline === null || baseline === void 0 ? void 0 : baseline.roles) === null || _a === void 0 ? void 0 : _a.find(function (r) { return r.role === mutation.role; });
            return stored
                ? __assign({ type: "addGlobal", role: mutation.role }, validityOf(stored)) : undefined;
        }
        case "addAuthorityType": {
            var createdId = result === null || result === void 0 ? void 0 : result.id;
            return createdId
                ? { type: "removeAuthorityType", roleId: createdId }
                : undefined;
        }
        case "updateAuthorityType": {
            var stored = standardRow(mutation.roleId);
            return stored
                ? {
                    type: "updateAuthorityType",
                    roleId: mutation.roleId,
                    authorityType: mutation.authorityType,
                    nkRole: stored.nkRole,
                    validFrom: stored.row.validFrom,
                    validTo: stored.row.validTo,
                }
                : undefined;
        }
        case "removeAuthorityType": {
            var stored = standardRow(mutation.roleId);
            return stored
                ? __assign({ type: "addAuthorityType", authorityType: (_b = stored.row.authorityType) !== null && _b !== void 0 ? _b : "", nkRole: stored.nkRole }, validityOf(stored.row)) : undefined;
        }
        default:
            return undefined;
    }
}
